// Angular
import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
// Rxjs
import {catchError, flatMap, map, switchMap, tap} from 'rxjs/operators';
import {Observable, of} from 'rxjs';
// Afidium
import {BookingResourceService} from 'libraries/service/resources/booking/bookingResource.service';
import {
    ITicketingReportSearchParameters,
    IBookingInTicketingReportSearchParametersUrl,
    TicketingReportSearchParameters
} from '../booking-in-ticketing-report/BookingInTicketingReportSearchParameters';
import {ResourceError} from 'libraries/service/resource/ResourceError';
import {ILabels} from 'libraries/interfaces/ILabels';
import {ISearchStatus} from 'app_sasha/shared/interfaces/ISearchStatus';
import {Yii} from 'libraries/interfaces/Yii';
import {IMomentDateFormat} from 'libraries/interfaces/IMomentDateFormat';
import {wenji} from 'libraries/interfaces/IWenji';
import {DateMomentPipe} from 'libraries/date/dateMoment.pipe';
import {TicketResourceService} from 'app_sasha/shared/resources/ticket/ticketResource.service';
import {
    IPostTicketsSearchRequest,
    IPostTicketsSearchResponse
} from 'app_sasha/shared/resources/ticket/interfaces/IPostTicketsSearch';
import {SashaPageComponent} from '../sasha-page.component';
import {ViewsService} from 'libraries/view/views.service';
import {AppRootService} from 'app/shared/services/app-root/app-root-service';
import {SashaBreadcrumbService} from 'app_sasha/shared/components/breadcrumb/sasha-breadcrumb.service';
import {TitleService} from 'app_sasha/shared/services/title/title.service';
import {ICompleteNumberLabels, LabelService} from 'libraries/service/label/label.service';
import {ErrorTracingService} from "libraries/service/error-tracing/error-tracing.service";
import {IGetBookingsCommonResponse} from 'libraries/service/resources/booking/interfaces/IGetBookings';

@Component({
    selector: 'booking-in-ticketing-report-search-result',
    template: `
        <div class="app-wrapper app-sasha">
            <sasha-breadcrumb></sasha-breadcrumb>
            <booking-results-container [title]="labels.title"
                                       [bookings]="(bookings ? bookings : [])"
                                       [isDisplayExport]="false"
                                       [searchStatus]="searchStatus">
            </booking-results-container>
        </div>
    `
})

export class BookingInTicketingReportSearchResultComponent extends SashaPageComponent implements OnInit {
    public dateFormat: IMomentDateFormat = JSON.parse(wenji.ui.dateFormat);

    public labels: ILabels = {
        title: Yii.t('wui-sasha-js', 'Etat de caisse du')
    };

    public bookings: IGetBookingsCommonResponse[];
    public isLoading: boolean;
    public error: string;
    public searchStatus: ISearchStatus = {
        isLoading: false,
        isError: false
    };

    private readonly titleLabels: ICompleteNumberLabels = {
        none: Yii.t("wui-sasha-js", "Aucun dossier"),
        one: Yii.t("wui-sasha-js", "1 dossier"),
        default: Yii.t("wui-sasha-js", "%placeholder% dossiers"),
    };

    constructor(
        protected views: ViewsService,
        appRootService: AppRootService,
        private readonly activatedRoute: ActivatedRoute,
        private readonly ticketResourceService: TicketResourceService,
        private readonly bookingResourceService: BookingResourceService,
        private readonly dateMomentPipe: DateMomentPipe,
        private readonly sashaBreadcrumbService: SashaBreadcrumbService,
        private readonly labelService: LabelService,
        private readonly titleService: TitleService,
        private readonly errorTracingService: ErrorTracingService,
    ) {
        super(views, appRootService);
    }

    public ngOnInit(): void {
        this.activatedRoute.queryParams.pipe(
            tap((searchParameters: IBookingInTicketingReportSearchParametersUrl) => {
                this.sashaBreadcrumbService.setBookingInTicketingReportResult(searchParameters);

                const max: string = this.dateMomentPipe.transform(searchParameters.max, 'ddd DD MMM YYYY');
                this.titleService.setTitle(Yii.t("wui-sasha-js", "État de caisse..."));

                this.labels.title = this.labelService.completeLabel(
                    `Etat de caisse du %max%`,
                    {
                        max
                    }
                );

                this.searchStatus = {
                    isLoading: true,
                    isError: false,
                };
            }),
            map((params: IBookingInTicketingReportSearchParametersUrl) => TicketingReportSearchParameters.fromRouteParams(params)),
            map(({system, agency, max}: ITicketingReportSearchParameters) => ({
                host: system,
                agency,
                whenIssued: {
                    max: max as string
                }
            })),
            switchMap((searchParameters: IPostTicketsSearchRequest) => this.ticketResourceService.getTickets(searchParameters)),
            map((tickets: IPostTicketsSearchResponse[]) => tickets
                // Filter booking without syscode
                .filter((ticket: IPostTicketsSearchResponse) => ticket.booking && ticket.booking.syscode)
                .map((ticket: IPostTicketsSearchResponse) => ticket.booking.syscode)
            ),
            map((syscodes: string[]) => [...new Set(syscodes)]),
            flatMap((syscodes: string[]): Observable<IGetBookingsCommonResponse[]> => {
                if (!syscodes.length) {
                    return of([]);
                }

                return this.bookingResourceService.getBookingsBySyscode(syscodes).pipe(
                    catchError((error: ResourceError | Error): Observable<IGetBookingsCommonResponse[]> => {
                        const resourceError: ResourceError = error as ResourceError;
                        if (resourceError.status === 404) {
                            return of(resourceError.data);
                        }

                        this.searchStatus = {
                            isError: true,
                            errorMessage: error.message,
                        };
                        this.titleService.setTitle(Yii.t("wui-sasha-js", "Erreur"), Yii.t("wui-sasha-js", "État de caisse"));

                        return of([]);
                    }),
                );
            })
        ).subscribe({
            next: (response: IGetBookingsCommonResponse[]): void => {
                this.bookings = response;

                if (!this.searchStatus.isError) {
                    this.titleService.setTitle(
                        this.labelService.completeNumber(this.bookings.length, this.titleLabels),
                        Yii.t("wui-sasha-js", "État de caisse")
                    );
                }

                this.searchStatus.isLoading = false;
            },
            error: (error: ResourceError | Error): void => {
                this.searchStatus.isLoading = false;
                this.searchStatus = {
                    isError: true,
                    errorMessage: error.message,
                };

                this.errorTracingService.captureException(error);
            }
        });
    }
}
